import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight, Search, Tag } from "lucide-react";
import { useTranslation } from "../context/LanguageContext";

interface ScanningEmptyStateProps {
  retailer: string;
  /** Online feeds keep the refresh card (with optional tips); physical stores get the app's scanning screen. */
  tips?: string[];
  isOnline?: boolean;
}

/**
 * Local deals are empty because the stores haven't been scanned yet (a new
 * plan or new stores). Mirrors pinto's ScanningStoresEmpty: promise deals
 * within 24 hours and offer Leads and Search in the meantime.
 */
export function ScanningStoresEmpty({ retailer }: { retailer: string }) {
  const { t } = useTranslation();
  const rows = [
    {
      key: "leads",
      icon: Tag,
      title: t("emptyStates.scanning.leadsTitle"),
      sub: t("emptyStates.scanning.leadsSub"),
      to: `/${retailer}/leads`,
    },
    {
      key: "search",
      icon: Search,
      title: t("emptyStates.scanning.searchTitle"),
      sub: t("emptyStates.scanning.searchSub"),
      to: `/${retailer}/product-search`,
    },
  ];

  return (
    <div className="flex w-full justify-center p-4 pt-10 md:p-8 md:pt-16">
      <div className="flex w-full max-w-[420px] flex-col items-center gap-[22px] px-1">
        <div className="relative flex h-20 w-20 items-center justify-center">
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full border-2 border-violet-600 motion-safe:animate-[scanning-ring_1.8s_ease-out_infinite]"
          />
          <img src="/app_icon.png" alt="" className="h-[66px] w-[66px] rounded-full" />
        </div>

        <div className="flex flex-col items-center gap-1.5 text-center">
          <h3 className="text-[23px] font-extrabold leading-7 tracking-tight text-[#1e1b3a] dark:text-white">
            {t("emptyStates.scanning.title")}
          </h3>
          <p className="text-[15.5px] font-semibold leading-[21px] text-[#6b6585] dark:text-[#b3adc7]">
            {t("emptyStates.scanning.body")}
          </p>
        </div>

        <div className="flex w-full flex-col gap-2">
          <p className="pl-1 text-[12.5px] font-extrabold tracking-[0.04em] text-[#6b6585] dark:text-[#b3adc7]">
            {t("emptyStates.scanning.meanwhile")}
          </p>
          <div className="overflow-hidden rounded-2xl border border-[#ECE8F3] bg-white dark:border-[#34303d] dark:bg-[#1e1b24]">
            {rows.map((row, index) => {
              const Icon = row.icon;
              return (
                <Link
                  key={row.key}
                  to={row.to}
                  className={`flex items-center gap-3 px-3.5 py-[13px] transition-colors hover:bg-violet-50/60 dark:hover:bg-white/5 ${
                    index < rows.length - 1 ? "border-b border-[#ECE8F3] dark:border-[#34303d]" : ""
                  }`}
                >
                  <span className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-[11px] bg-[#F1EAFB] text-[#6D28D9] dark:bg-[#2a2140] dark:text-[#C4B5FD]">
                    <Icon size={19} aria-hidden="true" />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col gap-px">
                    <span className="text-base font-extrabold text-[#1e1b3a] dark:text-white">{row.title}</span>
                    <span className="text-[13.5px] font-semibold leading-[18px] text-[#6b6585] dark:text-[#b3adc7]">{row.sub}</span>
                  </span>
                  <ChevronRight size={17} aria-hidden="true" className="flex-none text-[#6b6585] dark:text-[#b3adc7]" />
                </Link>
              );
            })}
          </div>
        </div>
      </div>
      <style>{`@keyframes scanning-ring { 0% { opacity: .7; transform: scale(.85); } 100% { opacity: 0; transform: scale(1.45); } }`}</style>
    </div>
  );
}

const ScanningEmptyState: React.FC<ScanningEmptyStateProps> = ({
  retailer,
  tips,
  isOnline = false,
}) => {
  const { t } = useTranslation();

  // Physical stores: the app's "Scanning your stores" screen.
  if (!isOnline) return <ScanningStoresEmpty retailer={retailer} />;

  const headline = "Fresh deals are loading";
  const description =
    "Your free online feed is refreshing. You do not need to add a store, choose a plan, or finish any setup.";
  const steps = ["Free online access ready", "Refreshing live prices…", "Deals appear automatically"];

  return (
    <div className="flex items-start justify-center w-full h-full p-4 md:p-8">
      <div className="w-full max-w-lg">
        {/* Radar animation */}
        <div className="flex justify-center mb-6">
          <div className="relative w-28 h-28">
            {/* Outer ring */}
            <div className="absolute inset-0 rounded-full border-2 border-primary-200 dark:border-primary-800/40 opacity-60" />
            {/* Middle ring */}
            <div className="absolute inset-3 rounded-full border-2 border-primary-300 dark:border-primary-700/50 opacity-50" />
            {/* Inner ring */}
            <div className="absolute inset-6 rounded-full border-2 border-primary-400 dark:border-primary-600/60 opacity-40" />
            {/* Sweep */}
            <div className="absolute inset-0 rounded-full animate-[spin_3s_linear_infinite] origin-center">
              <div
                className="absolute top-1/2 left-1/2 w-1/2 h-[2px] origin-left"
                style={{
                  background: "linear-gradient(90deg, var(--color-primary-500) 0%, transparent 100%)",
                }}
              />
              <div
                className="absolute top-1/2 left-1/2 w-1/2 h-full origin-left -translate-y-1/2"
                style={{
                  background: "conic-gradient(from 0deg, var(--color-primary-400) 0deg, transparent 30deg)",
                  borderRadius: "0 50% 50% 0",
                  opacity: 0.15,
                }}
              />
            </div>
            {/* Center dot */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative">
                <div className="w-3 h-3 bg-primary-500 rounded-full" />
                <div className="absolute inset-0 w-3 h-3 bg-primary-400 rounded-full animate-ping opacity-40" />
              </div>
            </div>
            {/* Blip dots */}
            <div className="absolute top-[22%] right-[26%] w-1.5 h-1.5 bg-emerald-400 rounded-full animate-[pulse_2s_ease-in-out_infinite_0.5s] opacity-80" />
            <div className="absolute bottom-[30%] left-[20%] w-1 h-1 bg-emerald-400 rounded-full animate-[pulse_2.5s_ease-in-out_infinite_1.2s] opacity-60" />
          </div>
        </div>

        {/* Headline */}
        <div className="text-center mb-6">
          <h3 className="text-xl font-bold text-gray-900 dark:text-dark-50 tracking-tight">
            {headline}
          </h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-dark-400 leading-relaxed max-w-sm mx-auto">
            {description}
          </p>
        </div>

        {/* Steps */}
        <div className="bg-white dark:bg-dark-900 border border-gray-200/80 dark:border-dark-700/60 rounded-xl p-4 mb-4 shadow-sm">
          <div className="space-y-3">
            {/* Step 1 - Done */}
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-100 dark:bg-emerald-500/15 flex items-center justify-center">
                <svg className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-sm font-medium text-gray-700 dark:text-dark-200">{steps[0]}</span>
            </div>

            {/* Step 2 - In progress */}
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-100 dark:bg-primary-500/15 flex items-center justify-center relative">
                <div className="w-2 h-2 bg-primary-500 rounded-full" />
                <div className="absolute inset-0 rounded-full border-2 border-primary-400/50 dark:border-primary-400/30 animate-[ping_2s_ease-in-out_infinite] opacity-40" />
              </div>
              <span className="text-sm font-medium text-primary-700 dark:text-primary-300">{steps[1]}</span>
            </div>

            {/* Step 3 - Pending */}
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 dark:bg-dark-800 flex items-center justify-center">
                <div className="w-2 h-2 bg-gray-300 dark:bg-dark-600 rounded-full" />
              </div>
              <span className="text-sm text-gray-400 dark:text-dark-500">{steps[2]}</span>
            </div>
          </div>
        </div>

        {/* Time estimate badge */}
        <div className="flex justify-center mb-4">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 dark:bg-amber-500/10 border border-amber-200/80 dark:border-amber-400/20 text-xs font-medium text-amber-700 dark:text-amber-300">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" />
            </svg>
            No store setup needed
          </div>
        </div>

        {/* Tips */}
        {tips && tips.length > 0 && (
          <div className="rounded-xl bg-gray-50 dark:bg-dark-900/50 border border-gray-200/60 dark:border-dark-700/40 p-4 mb-4">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-dark-500 font-semibold mb-2">
              {t("products.emptyStateTipsTitle")}
            </p>
            <ul className="space-y-1.5">
              {tips.map((tip) => (
                <li key={tip} className="flex items-start gap-2 text-[13px] text-gray-600 dark:text-dark-300">
                  <span className="mt-1.5 h-1 w-1 rounded-full bg-primary-400 flex-shrink-0" />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* CTA */}
        <Link
          to="/deals"
          className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 active:scale-[0.98] text-white text-sm font-medium transition-all shadow-sm shadow-primary-600/20"
        >
          Browse the live public feed
          <ArrowRight size={14} className="ml-auto opacity-60" />
        </Link>
      </div>
    </div>
  );
};

export default ScanningEmptyState;
